import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  LinearProgress,
  CircularProgress,
  Button,
  Rating,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  Skeleton,
} from "@mui/material";
import {
  MenuBook as BookIcon,
  Category as CategoryIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  LocalLibrary as LocalLibraryIcon,
  AutoStories as AutoStoriesIcon,
  WavingHand as WavingHandIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import {
  booksAPI,
  categoriesAPI,
  readingProgressAPI,
  readingSessionAPI,
} from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";
import CustomContainer from "../../components/common/CustomContainer";
import LazyImage from "../../components/common/LazyImage";

const getLocalizedCategoryName = (fullName, language) => {
  if (!fullName) return "";
  const parts = fullName.split("|");
  if (language === "en") return parts[0] || "";
  if (language === "fr") return parts[1] || parts[0] || "";
  if (language === "ar") return parts[2] || parts[0] || "";
  return parts[0] || "";
};

// ─── StatCard ────────────────────────────────────────────────────────────────

const STAT_CONFIGS = [
  {
    key: "totalBooks",
    icon: BookIcon,
    gradient: ["#1565c0", "#42a5f5"],
    bg: "rgba(21,101,192,0.08)",
  },
  {
    key: "totalCategories",
    icon: CategoryIcon,
    gradient: ["#6a1b9a", "#ab47bc"],
    bg: "rgba(106,27,154,0.08)",
  },
  {
    key: "readingTime",
    icon: TimeIcon,
    gradient: ["#1b5e20", "#66bb6a"],
    bg: "rgba(27,94,32,0.08)",
  },
  {
    key: "booksInProgress",
    icon: TrendingUpIcon,
    gradient: ["#e65100", "#ffa726"],
    bg: "rgba(230,81,0,0.08)",
  },
];

const StatCard = ({ title, value, statKey, loading }) => {
  const config = STAT_CONFIGS.find((c) => c.key === statKey) || STAT_CONFIGS[0];
  const Icon = config.icon;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: (th) =>
          th.palette.mode === "light" ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.07)",
        background: (th) =>
          th.palette.mode === "light" ? "#fff" : "#1a1a2e",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 8px 24px ${config.bg.replace("0.08", "0.18")}`,
        },
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* top gradient stripe */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${config.gradient[0]}, ${config.gradient[1]})`,
        }}
      />
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: config.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon
              sx={{
                fontSize: 24,
                color: config.gradient[0],
              }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={60} height={36} />
            ) : (
              <Typography
                sx={{
                  fontSize: "1.75rem",
                  fontWeight: 800,
                  lineHeight: 1,
                  background: `linear-gradient(135deg, ${config.gradient[0]}, ${config.gradient[1]})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {value}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── BookCard ────────────────────────────────────────────────────────────────

const BookCard = ({ book }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const [isRating, setIsRating] = useState(false);
  const [userRating, setUserRating] = useState(null);
  const [hovered, setHovered] = useState(false);

  const coverImageUrl = book.cover_image_path
    ? book.cover_image_path.startsWith("http")
      ? book.cover_image_path
      : `${import.meta.env.VITE_API_URL}${book.cover_image_path}`
    : null;

  useEffect(() => {
    if (!book?.id || !user?.id) return;
    booksAPI
      .getRatings(book.id)
      .then((res) => {
        const r = res.data?.ratings?.find(
          (r) => r.user_id === user.id || r.User?.id === user.id
        );
        if (r?.rating) {
          const v = parseInt(r.rating);
          if (!isNaN(v) && v > 0 && v <= 5) setUserRating(v);
        }
      })
      .catch(() => {});
  }, [book.id, user.id]);

  const handleRate = async (newValue) => {
    if (!newValue && userRating) return;
    try {
      setIsRating(true);
      const res = await booksAPI.rate(book.id, Math.round(newValue));
      const saved = parseInt(res.data?.rating?.rating || res.data?.rating);
      if (!isNaN(saved)) {
        setUserRating(saved);
        showSnackbar(t("books.rating.success"), "success");
      }
    } catch {
      showSnackbar(t("books.rating.error"), "error");
    } finally {
      setIsRating(false);
    }
  };

  return (
    <Card
      elevation={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        width: 200,
        minWidth: 200,
        flexShrink: 0,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: hovered
          ? alpha(theme.palette.primary.main, 0.25)
          : theme.palette.mode === "light"
          ? "rgba(0,0,0,0.07)"
          : "rgba(255,255,255,0.07)",
        background: theme.palette.mode === "light" ? "#fff" : "#1a1a2e",
        transition: "all 0.22s ease",
        transform: hovered ? "translateY(-5px)" : "none",
        boxShadow: hovered
          ? `0 12px 28px ${alpha(theme.palette.primary.main, 0.14)}`
          : "none",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
      }}
      onClick={() => navigate(`/books/${book.id}`)}
    >
      {/* Cover */}
      <Box
        sx={{
          height: 180,
          overflow: "hidden",
          position: "relative",
          background: alpha(theme.palette.primary.main, 0.06),
        }}
      >
        <LazyImage
          src={coverImageUrl}
          alt={book.title}
          sx={{
            height: "100%",
            transform: hovered ? "scale(1.06)" : "scale(1)",
            transition: "transform 0.3s ease",
          }}
        />
        {book.progress > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
            }}
          >
            <LinearProgress
              variant="determinate"
              value={book.progress}
              sx={{
                height: 3,
                background: "rgba(255,255,255,0.2)",
                "& .MuiLinearProgress-bar": {
                  background:
                    book.progress < 25
                      ? "#ffa726"
                      : book.progress < 75
                      ? "#42a5f5"
                      : "#66bb6a",
                },
              }}
            />
          </Box>
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 1.5, pb: 0.5 }}>
        <Typography
          noWrap
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: "text.primary",
            mb: 0.25,
          }}
        >
          {book.title}
        </Typography>
        <Typography
          noWrap
          sx={{ fontSize: "0.78rem", color: "text.secondary", mb: 0.75 }}
        >
          {book.author}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Rating
            value={userRating || 0}
            onChange={(_, v) => v && handleRate(Math.ceil(v))}
            disabled={isRating}
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{ "& .MuiRating-iconFilled": { color: "#ffa726" }, fontSize: "0.9rem" }}
          />
          <Typography sx={{ fontSize: "0.7rem", color: "text.disabled" }}>
            ({Number(book.average_rating || 0).toFixed(1)})
          </Typography>
        </Box>

        {book.progress > 0 && (
          <Typography
            sx={{ fontSize: "0.72rem", color: "text.secondary", mt: 0.5 }}
          >
            {book.progress}% {t("dashboard.progress")}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
        <Button
          size="small"
          fullWidth
          variant={hovered ? "contained" : "outlined"}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/books/${book.id}`);
          }}
          sx={{
            borderRadius: 6,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8rem",
            py: 0.5,
            boxShadow: "none",
          }}
        >
          {book.progress > 0
            ? t("books.actions.continue")
            : t("books.actions.start")}
        </Button>
      </CardActions>
    </Card>
  );
};

// ─── SectionHeader ────────────────────────────────────────────────────────────

const SectionHeader = ({ title, viewAllLink, count }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 4,
            height: 22,
            borderRadius: 2,
            background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: "text.primary",
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </Typography>
        {count > 0 && (
          <Chip
            label={count}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 700,
              background: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              border: "none",
            }}
          />
        )}
      </Box>

      {viewAllLink && (
        <Button
          component={Link}
          to={viewAllLink}
          size="small"
          endIcon={<ArrowForwardIcon sx={{ fontSize: "0.85rem !important" }} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8rem",
            color: "primary.main",
            "&:hover": { background: alpha(theme.palette.primary.main, 0.06) },
          }}
        >
          {t("common.viewAll")}
        </Button>
      )}
    </Box>
  );
};

// ─── HorizontalScroll ────────────────────────────────────────────────────────

const HorizontalScroll = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        overflowX: "auto",
        gap: 2,
        pb: 1.5,
        "&::-webkit-scrollbar": { height: 6 },
        "&::-webkit-scrollbar-track": {
          background: alpha(theme.palette.divider, 0.3),
          borderRadius: 3,
        },
        "&::-webkit-scrollbar-thumb": {
          background: alpha(theme.palette.primary.main, 0.25),
          borderRadius: 3,
          "&:hover": { background: alpha(theme.palette.primary.main, 0.45) },
        },
      }}
    >
      {children}
    </Box>
  );
};

// ─── EmptyState ───────────────────────────────────────────────────────────────

const EmptyState = ({ icon, title, description, action }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 5,
        px: 2,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: alpha(theme.palette.primary.main, 0.08),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        {React.cloneElement(icon, {
          sx: { fontSize: 36, color: "primary.main" },
        })}
      </Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.75, fontSize: "0.95rem" }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 360, mb: 2.5, fontSize: "0.85rem" }}
      >
        {description}
      </Typography>
      {action}
    </Box>
  );
};

// ─── Section container ────────────────────────────────────────────────────────

const Section = ({ children, sx = {} }) => (
  <Box
    sx={{
      background: (th) =>
        th.palette.mode === "light" ? "rgba(0,0,0,0.018)" : "rgba(255,255,255,0.025)",
      borderRadius: 3,
      p: 3,
      mb: 3,
      border: "1px solid",
      borderColor: (th) =>
        th.palette.mode === "light" ? "rgba(0,0,0,0.055)" : "rgba(255,255,255,0.055)",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// ─── Dashboard ────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const { t, i18n } = useTranslation("common");
  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [pinnedCategories, setPinnedCategories] = useState([]);
  const [categoryBooks, setCategoryBooks] = useState({});
  const [inProgressBooks, setInProgressBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBooks: 0,
    totalCategories: 0,
    readingTime: 0,
    booksInProgress: 0,
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [statsRes, pinnedRes] = await Promise.all([
          booksAPI.getDashboardStats(),
          categoriesAPI.getPinned(),
        ]);

        const {
          totalBooks = 0,
          totalCategories = 0,
          booksInProgress = 0,
          readingTime = 0,
        } = statsRes.data?.stats || {};

        const pinnedCats = pinnedRes.data?.categories || [];
        const inProgress = statsRes.data?.inProgressBooks || [];

        const catBooks = {};
        await Promise.all(
          pinnedCats.map(async (cat) => {
            try {
              const booksRes = await categoriesAPI.getBooks(cat.id);
              const books = booksRes.data?.category?.books || [];
              catBooks[cat.id] = await Promise.all(
                books.map(async (book) => {
                  try {
                    const progRes = await readingProgressAPI.get(book.id);
                    const pd = progRes.data?.progress;
                    const pct =
                      pd?.current_page && pd.Book?.total_pages
                        ? Math.ceil((pd.current_page / pd.Book.total_pages) * 100)
                        : 0;
                    return {
                      ...book,
                      progress: pct,
                      completed: pd?.completed || false,
                      Category: book.Category || { id: cat.id, name: cat.name },
                    };
                  } catch {
                    return {
                      ...book,
                      progress: 0,
                      Category: book.Category || { id: cat.id, name: cat.name },
                    };
                  }
                })
              );
            } catch {
              catBooks[cat.id] = [];
            }
          })
        );

        setPinnedCategories(pinnedCats);
        setCategoryBooks(catBooks);
        setInProgressBooks(inProgress);
        setStats({ totalBooks, totalCategories, readingTime, booksInProgress });
      } catch {
        showSnackbar(t("dashboard.errors.loadFailed"), "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [showSnackbar, t, user.id]);

  const firstName = user.first_name || user.username;
  const hour = new Date().getHours();
  const timeKey =
    hour < 12
      ? t("dashboard.greeting_morning", "Good morning")
      : hour < 18
      ? t("dashboard.greeting_afternoon", "Good afternoon")
      : t("dashboard.greeting_evening", "Good evening");

  return (
    <CustomContainer>
      {/* ── Hero greeting ──────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 4,
          mt: 0.5,
          p: 3,
          borderRadius: 3,
          background:
            theme.palette.mode === "light"
              ? "linear-gradient(135deg, #e8f0fe 0%, #f3e5f5 100%)"
              : "linear-gradient(135deg, rgba(21,101,192,0.18) 0%, rgba(106,27,154,0.14) 100%)",
          border: "1px solid",
          borderColor:
            theme.palette.mode === "light"
              ? "rgba(21,101,192,0.12)"
              : "rgba(255,255,255,0.06)",
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #1565c0, #42a5f5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AutoStoriesIcon sx={{ color: "#fff", fontSize: 26 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.15rem", md: "1.35rem" },
              color: "text.primary",
              lineHeight: 1.2,
            }}
          >
            {timeKey}, {firstName}
          </Typography>
          <Typography
            sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 0.25 }}
          >
            {loading
              ? t("dashboard.loading", "Loading your library…")
              : t("dashboard.welcomeMessage")}
          </Typography>
        </Box>
      </Box>

      {/* ── Stats ──────────────────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {[
          {
            statKey: "totalBooks",
            title: t("dashboard.stats.totalBooks"),
            value: stats.totalBooks,
          },
          {
            statKey: "totalCategories",
            title: t("dashboard.stats.categories"),
            value: stats.totalCategories,
          },
          {
            statKey: "readingTime",
            title: t("dashboard.stats.readingTime"),
            value: readingSessionAPI.formatDuration(stats.readingTime),
          },
          {
            statKey: "booksInProgress",
            title: t("dashboard.stats.inProgress"),
            value: stats.booksInProgress,
          },
        ].map((s) => (
          <Grid item xs={12} sm={6} md={3} key={s.statKey}>
            <StatCard {...s} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* ── In-progress books ──────────────────────────────────────── */}
      <Section>
        <SectionHeader
          title={t("dashboard.sections.booksInProgress")}
          count={inProgressBooks.length}
          viewAllLink="/books"
        />
        {loading ? (
          <Box sx={{ display: "flex", gap: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={200}
                height={280}
                sx={{ borderRadius: 3, flexShrink: 0 }}
              />
            ))}
          </Box>
        ) : inProgressBooks.length > 0 ? (
          <HorizontalScroll>
            {inProgressBooks.map((book) => (
              <BookCard key={book.id} book={book} />
            ))}
          </HorizontalScroll>
        ) : (
          <EmptyState
            icon={<LocalLibraryIcon />}
            title={t("dashboard.messages.noInProgressBooks")}
            description={t("dashboard.messages.noInProgressDescription")}
            action={
              <Button
                variant="contained"
                onClick={() => navigate("/books")}
                startIcon={<BookIcon />}
                sx={{ borderRadius: 6, textTransform: "none", px: 3, fontWeight: 600 }}
              >
                {t("dashboard.actions.browseBooks")}
              </Button>
            }
          />
        )}
      </Section>

      {/* ── Pinned categories ──────────────────────────────────────── */}
      <Section>
        <SectionHeader
          title={t("dashboard.sections.pinnedCategories")}
          viewAllLink="/categories"
          count={pinnedCategories.length}
        />

        {loading ? (
          <Box sx={{ display: "flex", gap: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={200}
                height={280}
                sx={{ borderRadius: 3, flexShrink: 0 }}
              />
            ))}
          </Box>
        ) : pinnedCategories.length > 0 ? (
          pinnedCategories.map((category) => {
            const books = categoryBooks[category.id] || [];
            if (!books.length) return null;
            return (
              <Box key={category.id} sx={{ mb: 4 }}>
                <SectionHeader
                  title={getLocalizedCategoryName(category.name, i18n.language)}
                  viewAllLink={`/categories/${category.id}`}
                  count={category.bookcount || books.length}
                />
                <HorizontalScroll>
                  {books.map((book) => (
                    <BookCard key={book.id} book={book} />
                  ))}
                </HorizontalScroll>
              </Box>
            );
          })
        ) : (
          <EmptyState
            icon={<CategoryIcon />}
            title={t("dashboard.messages.noPinnedCategories")}
            description={t("dashboard.messages.noPinnedDescription")}
            action={
              <Button
                variant="contained"
                onClick={() => navigate("/categories")}
                startIcon={<CategoryIcon />}
                sx={{ borderRadius: 6, textTransform: "none", px: 3, fontWeight: 600 }}
              >
                {t("dashboard.actions.browseCategories")}
              </Button>
            }
          />
        )}
      </Section>
    </CustomContainer>
  );
};

export default Dashboard;
